import React, { useReducer, useEffect, useState, useRef, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { difference, cloneDeep, isEmpty, noop } from 'lodash'

import { useForm, FormProvider } from 'react-hook-form'
import { shallowEqual, useSelector, useDispatch } from 'react-redux'
import { Box, DialogContent } from '@mui/material'

import { get } from 'utils/lodash'
import { handleError, handleErrorWithFetchError } from 'utils/error'
import { calculateSum, formatCentsToDollars, formatDollarsToCents } from 'utils/price'
import { handleInvoiceSelectDeselect, removeInvoiceAmountFromPayload } from 'utils/payment'
import { useLazyGetIsSurchargeAppliedQuery, useLazyGetAllPaymentMethodsQuery } from 'api/payment-gateway/paymentGateway'
import { useLazyGetAccountDetailsQuery } from 'api/accounts/getAccountDetails'
import { useLazyGetAccountInvoicesQuery } from 'api/accounts/getAccountInvoices'
import {
  getPaymentMethodByStatus,
  getAllSortedUnpaidInvoices,
  checkHasZeroOrNegativeInvoices,
  getSinglePaymentPayload,
  addUpdateRequestedPayments,
  getEditPaymentModel,
} from 'utils/takePayments'
import { PAYMENT_METHODS } from 'settings/constants/payment'
import { usePostPaymentsMutation } from 'api/billing/postPayments'
import { useAddPaymentBatchesToGroupMutation } from 'api/groups/payment/addPaymentBatchesToGroup'
import { putIsLoading } from 'middleware/actions/response'
import { getCustomerDetails, putCustomerDetails } from 'middleware/actions/customers'
import {
  CACHE_TAG_BILLING_PAYMENT_GROUP_DETAILS,
  CACHE_TAG_BILLING_PAYMENT_LIST,
  CACHE_TAG_INVOICE_LIST,
  CACHE_TAG_ACCOUNT_RECEIVABLES_SUMMARY,
  CACHE_TAG_ACCOUNT_INVOICE_LIST,
  CACHE_TAG_ACCOUNT_PAYMENT_LIST,
  CACHE_TAG_ACCOUNT_LIST,
  CACHE_TAG_ACCOUNT_DETAILS_BY_ID,
  CACHE_TAG_ACCOUNT_GROUP_DETAILS,
  CACHE_TAG_AGING_GROUP_DETAILS,
  CACHE_TAG_SUSPENSION_GROUP_DETAILS,
  CACHE_TAG_ACCOUNT_PAYMENT_METHOD_LIST,
} from 'api/cacheTagTypes'
import { setBatchConfirmationDialogState } from 'slices/groups/PaymentGroupsSlice'
import { useAccountingLockValue } from 'providers/AccountingLockProvider'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { isAllowNegativeInvoiceFeatureAvailable } from 'data/launch-darkly/featureSelectors'

import T from 'T'
import api from 'api'
import { isCustomerDetailsPage } from 'router/routes'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHDialogTitle from 'components/common/HHDialogTitle'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import Summary from 'components/billing/groups/payment/take-payments/Summary'
import DialogActions from 'components/billing/groups/payment/take-payments/DialogActions'
import { PAYMENT_MODEL, SEARCH_TYPES, PAYMENT_FORM_MODEL } from 'components/billing/groups/payment/take-payments/model'
import { PAYMENT_BATCH_CONFIRMATION_TIMEOUT } from 'components/billing/groups/payment/take-payments/settings'
import { getInvoices, preparePayload } from 'components/billing/groups/payment/take-payments/helper'
import EditBillingLocationModal from 'components/customer-details/content/billing-location-details/EditBillingLocationModal'
import { combineAddressInfo, isAddressObjectEmpty } from 'utils/helper'
import HHConfirmDialog from 'components/common/HHConfirmDialog'
import SearchLayout from './layout/SearchLayout'
import ListLayout from './layout/ListLayout'
import { useLazyGetOverridingBeneficialOwnerContactQuery } from 'api/contact/beneficialOwnersCrud'
import EditBeneficialOwnerLocationDialog from 'components/customer-details/content/beneficial_owner_contacts/EditBeneficialOwnerLocationDialog'
import usePaymentProvider from 'components/customer-details/content/payment-details/usePaymentProvider'

const { ACCOUNT_CREDIT, CHECK } = PAYMENT_METHODS
const FIRST_STEP_INDEX = 0

const TakePaymentsDialog = ({
  isOpen = false,
  initialAccount = {},
  initialGroupId = '',
  existingAccounts = [],
  onClose,
  callback = noop,
}) => {
  const dispatch = useDispatch()
  const canTakePayment = useSelector(state => get(state, 'responseSlice.canTakePayment', true), shallowEqual)
  const [getIsSurchargeApplied, { data: hasSurcharge }] = useLazyGetIsSurchargeAppliedQuery()
  const [getAllPaymentMethods, { data: paymentMethodsData, isFetching: isLoadingGetAllPaymentMethods }] = useLazyGetAllPaymentMethodsQuery()
  const [getAccountDetails, { data: accountRawData, isFetching: isLoadingGetAccountDetails, isSuccess: isSuccessGetAccountDetails }] =
    useLazyGetAccountDetailsQuery()
  const accountData = useMemo(() => (isSuccessGetAccountDetails ? accountRawData : undefined), [accountRawData, isSuccessGetAccountDetails])
  const [getAccountInvoices, { data: invoiceRawData, isFetching: isLoadingGetAccountInvoices, isSuccess: isSuccessGetAccountInvoices }] =
    useLazyGetAccountInvoicesQuery()
  const invoiceData = useMemo(
    () => (isSuccessGetAccountInvoices ? invoiceRawData : undefined),
    [invoiceRawData, isSuccessGetAccountInvoices]
  )
  const [postPayments, { isLoading: isPostPaymentLoading }] = usePostPaymentsMutation()
  const [addPaymentBatchesToGroup] = useAddPaymentBatchesToGroupMutation()
  const isLoadingResponse = useSelector(s => get(s, 'ResponseReducer.isLoading', false))
  const [isOpenEditBillingLocationDialog, setIsOpenEditBillingLocationDialog] = useState(false)
  const [isOpenEditBeneficialLocationDialog, setIsOpenEditBeneficialLocationDialog] = useState(false)

  const hasExistingAccounts = existingAccounts.length > 0
  const hasInitialAccount = !isEmpty(initialAccount)
  const paymentForm = useForm({ defaultValues: { ...PAYMENT_FORM_MODEL } })
  const { formState, setValue, reset, watch, getValues, setFocus, clearErrors, handleSubmit } = paymentForm
  const watchPaymentMethod = watch('paymentMethod')
  const watchAmount = watch('amount')
  const watchIsNewCard = watch('isNewCard')
  const [isOpenConfirmCloseDialog, setIsOpenConfirmCloseDialog] = useState(false)
  const [activeStep, setActiveStep] = useState(FIRST_STEP_INDEX)
  const searchRef = useRef(null)
  const [localState, setLocalState] = useReducer((prevState, newState) => ({ ...prevState, ...newState }), {
    batchDate: new Date(),
    groupId: initialGroupId,
    editPaymentRefId: '',
    requestedPayments: [],
    ...PAYMENT_MODEL,
  })
  const [getOverridingBeneficialOwner, { data: beneficialOwnerContact }] = useLazyGetOverridingBeneficialOwnerContactQuery()
  const beneficialOwnerId = useMemo(() => get(beneficialOwnerContact, 'id'), [beneficialOwnerContact])
  const { batchDate, groupId, editPaymentRefId, requestedPayments, searchType, selectedOption, invoicesToPay = [] } = localState
  const isCalledFromCustomerDetailsPage = isCustomerDetailsPage()
  const accountId = selectedOption?.accountId || selectedOption?.id
  const isFirstStep = activeStep === FIRST_STEP_INDEX
  const isCustomerSearch = searchType === SEARCH_TYPES.CUSTOMER
  const isInvoiceSearch = searchType === SEARCH_TYPES.INVOICE
  const { isCreditCard, isCreditCardOrACH, isAccountCredit } = getPaymentMethodByStatus(watchPaymentMethod)
  const apiRef = useGridApiRef()
  const { checkIsLocked } = useAccountingLockValue()
  const isBeneficialOwnerAddress = useMemo(() => !!beneficialOwnerId, [beneficialOwnerId])
  const savedPaymentMethods = get(paymentMethodsData, 'paymentMethods', [])
  const invoiceList = getInvoices(searchType, selectedOption, invoiceData)
  const sortedUnpaidInvoices = getAllSortedUnpaidInvoices(invoiceList, checkIsLocked)
  const selectionModel = invoicesToPay?.map(({ invoiceNumber }) => invoiceNumber)
  const flags = useFlags()
  const isAllowNegativeInvoiceEnabled = isAllowNegativeInvoiceFeatureAvailable({ flags })
  const initialCreditBalanceCents = selectedOption ? get(accountData, 'customerCreditBalanceCents', 0) : 0

  // Fortis programmatic tokenization — provider follows the selected account's business unit
  const { isFortis } = usePaymentProvider(accountId)
  const fortisSubmitRef = useRef(null)
  const [isFortisLoading, setIsFortisLoading] = useState(false)

  const getUpdatedCreditBalance = () => {
    // If no invoices to pay, add watch amount to initial balance
    if (!invoicesToPay?.length) {
      return initialCreditBalanceCents + formatDollarsToCents(watchAmount || 0)
    }

    // Otherwise, add up all extra balances from invoices
    const totalExtraBalance = calculateSum(invoicesToPay, 'extraBalance')

    return initialCreditBalanceCents + totalExtraBalance
  }

  const updatedCreditBalanceCents = getUpdatedCreditBalance()

  const isLoading = isLoadingGetAccountDetails || isLoadingGetAccountInvoices || isLoadingGetAllPaymentMethods || !canTakePayment
  const isPostLoading = isLoading || isLoadingResponse || isPostPaymentLoading

  // Keep account identity in a ref to avoid stale closures across multi-click/tokenization flows
  const accountIdentityRef = useRef({
    accountId: '',
    customerName: '',
    accountNumber: '',
  })

  useEffect(() => {
    accountIdentityRef.current = {
      accountId: selectedOption?.accountId || selectedOption?.id || '',
      customerName: get(accountData, 'accountName', ''),
      accountNumber: get(accountData, 'accountNumber', ''),
    }
  }, [selectedOption, accountData])

  const getAccountFields = useCallback(() => {
    return accountIdentityRef.current
  }, [])

  const handleClose = (showConfirmDialog = true) => {
    if (requestedPayments.length !== 0 && showConfirmDialog) {
      setIsOpenConfirmCloseDialog(true)
    } else {
      onClose()
    }
  }

  const handleChange = (key, value) => setLocalState({ [key]: value })

  const handleInvoiceSelection = (invoices, addedAmount, newlySelectedInvoiceNumbers = [], showToastMessages = true) => {
    const accountCreditAmount = getValues('accountCreditAmount')
    const effectiveCreditBalance = formatDollarsToCents(accountCreditAmount)

    const result = handleInvoiceSelectDeselect(
      invoices,
      effectiveCreditBalance,
      formatDollarsToCents(addedAmount),
      newlySelectedInvoiceNumbers,
      sortedUnpaidInvoices,
      showToastMessages
    )

    const processedInvoices = result.selectedInvoices || []

    return { selectedInvoices: processedInvoices }
  }

  const handleResetPaymentTypeFields = () => {
    setValue('paymentDate', new Date())
    setValue('checkNum', '')
    setValue('amount', 0)
    setValue('selectedPaymentTokenDetails', {})
    setValue('isNewCard', false)
    setValue('cardHolderName', '')
    setValue('cvv', '')
    setValue('isSaveNewMethod', false)
    setValue('cardNickName', '')
    setValue('autoPayTime', null)
    setValue('note', '')
    clearErrors()
  }

  const fetchAllPaymentMethods = id => {
    getAllPaymentMethods({ accountId: id }).unwrap().catch(handleError)
  }

  const handleAmountChange = (value, isFromCreditToggle = false, creditAmount = 0) => {
    const applyAccountCredit = getValues('applyAccountCredit')
    const numericValue = value
    if (isFromCreditToggle || applyAccountCredit) {
      const effectiveCreditAmount = isFromCreditToggle ? creditAmount : getValues('accountCreditAmount')

      // If there's no payment and no credit, deselect all invoices
      if (numericValue === 0 && effectiveCreditAmount === 0) {
        setLocalState({ invoicesToPay: [] })
        return
      }

      // Select invoices based on combined payment and credit amount
      const allInvoicesForSelection = sortedUnpaidInvoices.map(invoice => ({
        invoiceNumber: invoice.invoiceNumber,
        invoiceAmount: invoice.invoiceAmount,
        isLocked: invoice.isLocked,
      }))

      const { selectedInvoices } = handleInvoiceSelection(allInvoicesForSelection, numericValue, [], false)
      setLocalState({ invoicesToPay: selectedInvoices })
    } else {
      const allInvoicesForSelection = sortedUnpaidInvoices.map(invoice => ({
        invoiceNumber: invoice.invoiceNumber,
        invoiceAmount: invoice.invoiceAmount,
        isLocked: invoice.isLocked,
      }))

      const { selectedInvoices } = handleInvoiceSelection(allInvoicesForSelection, value, [], false)
      setLocalState({ invoicesToPay: selectedInvoices })
    }
  }

  const handleAccountCreditAmountChange = value => {
    const allInvoicesForSelection = sortedUnpaidInvoices.map(invoice => ({
      invoiceNumber: invoice.invoiceNumber,
      invoiceAmount: invoice.invoiceAmount,
      isLocked: invoice.isLocked,
    }))

    const { selectedInvoices } = handleInvoiceSelection(allInvoicesForSelection, getValues('amount'), [], false)
    setLocalState({ invoicesToPay: selectedInvoices || [] })
  }

  const prefillInvoices = useCallback(
    (response, invoices) => {
      const unpaidInvoices = getAllSortedUnpaidInvoices(invoices, checkIsLocked)
      const customerCreditBalanceCents = get(response, 'customerCreditBalanceCents', 0)
      const invoiceNumber = get(initialAccount, 'invoiceNumber')
      if (hasInitialAccount && invoiceNumber) {
        const index = unpaidInvoices.findIndex(invoice => invoice.invoiceNumber === invoiceNumber)
        const selectedInvoice = unpaidInvoices[index]
        const { invoiceAmount } = selectedInvoice
        const neededAmountCents = Math.max(0, invoiceAmount - customerCreditBalanceCents)
        apiRef?.current?.scrollToIndexes({ rowIndex: index })
        const { selectedInvoices } = handleInvoiceSelection([{ ...selectedInvoice }], formatCentsToDollars(neededAmountCents), [], false)
        setLocalState({ invoicesToPay: selectedInvoices })
        setValue('paymentMethod', neededAmountCents > 0 ? CHECK : ACCOUNT_CREDIT)
        setValue('amount', formatCentsToDollars(neededAmountCents))
        if (neededAmountCents > 0) {
          setTimeout(() => setFocus('checkNum'), 100)
        }
        return
      }

      if (customerCreditBalanceCents > 0 && unpaidInvoices.length > 0) {
        setValue('applyAccountCredit', true)
        const totalInvoiceAmountCents = unpaidInvoices.reduce(
          (sum, invoice) => sum + (invoice.usedPartialAmount || invoice.invoiceAmount),
          0
        )
        setValue('paymentMethod', ACCOUNT_CREDIT)
        const defaultCreditAmountCents = Math.min(customerCreditBalanceCents, totalInvoiceAmountCents)
        setValue('accountCreditAmount', formatCentsToDollars(defaultCreditAmountCents))
        setValue('defaultAccountCreditAmount', formatCentsToDollars(defaultCreditAmountCents))

        const allInvoicesForSelection = unpaidInvoices.map(invoice => ({
          invoiceNumber: invoice.invoiceNumber,
          invoiceAmount: invoice.invoiceAmount,
          isLocked: invoice.isLocked,
        }))

        const { selectedInvoices } = handleInvoiceSelection(allInvoicesForSelection, 0, [], false)
        setLocalState({ invoicesToPay: selectedInvoices })
      } else if (isAllowNegativeInvoiceEnabled && checkHasZeroOrNegativeInvoices(unpaidInvoices)) {
        const allNegativeInvoicesForSelection = unpaidInvoices.map(invoice => ({
          invoiceNumber: invoice.invoiceNumber,
          invoiceAmount: invoice.invoiceAmount,
          isLocked: invoice.isLocked,
        }))

        const { selectedInvoices: selectedNegativeInvoices } = handleInvoiceSelection(allNegativeInvoicesForSelection, 0, [], false)
        setLocalState({ invoicesToPay: selectedNegativeInvoices })
        setValue('paymentMethod', ACCOUNT_CREDIT)
        setValue('amount', 0)
      } else {
        setValue('paymentMethod', CHECK)
        setTimeout(() => setFocus('checkNum'), 100)
      }
    },
    [hasInitialAccount, initialAccount, apiRef, sortedUnpaidInvoices]
  )

  const handleSearchChange = async selection => {
    setLocalState({ selectedOption: selection, invoicesToPay: [] })
    setValue('paymentMethod', '')
    handleResetPaymentTypeFields()
    const newAccountId = selection?.accountId || selection?.id
    if (!newAccountId) {
      return
    }
    let invoices = []
    if (isCustomerSearch && get(selection, 'unpaidInvoiceCount', 0) > 0) {
      const result = await getAccountInvoices({ accountId: newAccountId }).unwrap().catch(handleError)
      invoices = get(result, 'invoices', [])
    } else if (isInvoiceSearch) {
      invoices = [selection]
    }

    getAccountDetails({ accountId: newAccountId })
      .unwrap()
      .then(response => prefillInvoices(response, invoices))
      .catch(error => {
        handleErrorWithFetchError(error, 'There was a problem fetching account details. Please try again.')
        handleChange('searchValue', '')
        handleSearchChange(null)
      })

    fetchAllPaymentMethods(newAccountId)
  }

  const handleSelectAllClick = event => {
    if (event.target.checked) {
      const allInvoicesForSelection = sortedUnpaidInvoices.map(invoice => ({
        invoiceNumber: invoice.invoiceNumber,
        invoiceAmount: invoice.invoiceAmount,
        isLocked: invoice.isLocked,
      }))

      const { selectedInvoices } = handleInvoiceSelection(
        allInvoicesForSelection,
        watchAmount,
        sortedUnpaidInvoices.map(invoice => invoice.invoiceNumber),
        true
      )
      setLocalState({ invoicesToPay: selectedInvoices })
      return
    }

    setLocalState({ invoicesToPay: [] })
  }

  const handleRowCheckboxClick = newSelectionModel => {
    if (isLoading) {
      return
    }

    if (newSelectionModel.length === 0) {
      setLocalState({ invoicesToPay: [] })
      return
    }

    const selectedIds = difference(newSelectionModel, selectionModel)
    const unselectedIds = difference(selectionModel, newSelectionModel)

    let newSelected = []
    let newlySelectedInvoiceNumber = null

    if (selectedIds.length) {
      const selectedInvoice = sortedUnpaidInvoices.find(invoice => invoice.invoiceNumber === selectedIds[0])

      if (!selectedInvoice) {
        return
      }

      const { invoiceNumber, invoiceAmount, isLocked } = selectedInvoice
      newSelected = [...invoicesToPay, { invoiceNumber, invoiceAmount, isLocked }]
      newlySelectedInvoiceNumber = invoiceNumber
    } else {
      newSelected = invoicesToPay.filter(row => row.invoiceNumber !== unselectedIds[0])
    }

    const { selectedInvoices } = handleInvoiceSelection(
      newSelected,
      watchAmount,
      newlySelectedInvoiceNumber ? [newlySelectedInvoiceNumber] : []
    )

    setLocalState({ invoicesToPay: selectedInvoices })
  }

  const handleClearClick = () => {
    handleResetPaymentTypeFields()
    handleAmountChange(0, isAccountCredit)
    if (hasInitialAccount) {
      setLocalState({ groupId: '' })
    }
  }

  const handleRefetch = () => {
    const invalidateTags = hasExistingAccounts
      ? [CACHE_TAG_ACCOUNT_INVOICE_LIST, CACHE_TAG_ACCOUNT_PAYMENT_LIST]
      : [
          { type: CACHE_TAG_ACCOUNT_INVOICE_LIST, id: accountId },
          { type: CACHE_TAG_ACCOUNT_PAYMENT_LIST, id: accountId },
        ]

    dispatch(
      api.util.invalidateTags([
        CACHE_TAG_BILLING_PAYMENT_LIST,
        CACHE_TAG_INVOICE_LIST,
        CACHE_TAG_ACCOUNT_RECEIVABLES_SUMMARY,
        ...invalidateTags,
        CACHE_TAG_ACCOUNT_LIST,
        { type: CACHE_TAG_ACCOUNT_DETAILS_BY_ID, id: accountId },
        CACHE_TAG_ACCOUNT_GROUP_DETAILS,
        CACHE_TAG_AGING_GROUP_DETAILS,
        CACHE_TAG_SUSPENSION_GROUP_DETAILS,
      ])
    )

    if (isCalledFromCustomerDetailsPage) {
      dispatch(getCustomerDetails({ accountId }))

      if (isCreditCardOrACH && watchIsNewCard) {
        dispatch(api.util.invalidateTags([{ type: CACHE_TAG_ACCOUNT_PAYMENT_METHOD_LIST, id: accountId }]))
      }
    }
  }

  const handleAddBatchToGroup = batchId => {
    addPaymentBatchesToGroup({ id: groupId, batchIds: [batchId], skipInvalidate: true })
      .unwrap()
      .catch(handleError)
  }

  const handlePostPayments = payments => {
    if (isPostLoading) return
    dispatch(putIsLoading(true))
    const payload = preparePayload(payments, hasInitialAccount, batchDate)

    postPayments(removeInvoiceAmountFromPayload(payload))
      .unwrap()
      .then(async result => {
        const batchId = result?.batchId
        const hasBatchIdAndGroupId = batchId && groupId
        if (hasBatchIdAndGroupId) {
          handleAddBatchToGroup(batchId)
        }

        toast.info(hasInitialAccount ? T.PAYMENT_PROCESSING : T.PAYMENT_BATCH_PROCESSING, {
          hideProgressBar: false,
          autoClose: hasInitialAccount ? 5000 : PAYMENT_BATCH_CONFIRMATION_TIMEOUT,
          onClose: () => {
            if (hasBatchIdAndGroupId) {
              dispatch(api.util.invalidateTags([{ type: CACHE_TAG_BILLING_PAYMENT_GROUP_DETAILS, id: groupId }]))
            }
            handleRefetch()
            callback()
          },
        })

        if (batchId && !hasInitialAccount) {
          // Only open for batch payments
          dispatch(setBatchConfirmationDialogState({ isOpen: true, batchId, groupId, batchDate }))
        }
        handleClose(false)
      })
      .catch(handleError)
      .finally(() => dispatch(putIsLoading(false)))
  }

  const handleNewPaymentClick = () => {
    setLocalState({ ...cloneDeep(PAYMENT_MODEL), editPaymentRefId: '' })
    reset(cloneDeep(PAYMENT_FORM_MODEL))
    setActiveStep(FIRST_STEP_INDEX)
  }

  const handleEditClick = async (refId, editAccountId) => {
    const existingPaymentDetails = requestedPayments.find(payment => payment.refId === refId)
    const { stateData, formData } = getEditPaymentModel(existingPaymentDetails)

    try {
      dispatch(putIsLoading(true))

      if (stateData.searchType === SEARCH_TYPES.CUSTOMER) {
        await getAccountInvoices({ accountId: editAccountId }).unwrap()
      }
      await getAccountDetails({ accountId: editAccountId }).unwrap()

      setLocalState({ editPaymentRefId: refId, ...stateData })
      reset(formData)

      fetchAllPaymentMethods(editAccountId)
      setActiveStep(FIRST_STEP_INDEX)
    } catch (error) {
      handleError(error)
    } finally {
      dispatch(putIsLoading(false))
    }
  }

  const handleRemoveClick = (refId, invoiceNumber, totalInvoices) => {
    if (totalInvoices <= 1) {
      // If we have just one invoice per customer directly remove complete record
      // Or no selected invoice
      setLocalState({ requestedPayments: requestedPayments.filter(payment => payment.refId !== refId) })
      return
    }

    setLocalState({
      requestedPayments: requestedPayments.map(payment => {
        if (payment.refId === refId) {
          // b/c of partial payment recalculate once we delete any row
          const { selectedInvoices } = handleInvoiceSelectDeselect(
            payment.invoicesToPay?.filter(invoice => invoice.invoiceNumber !== invoiceNumber),
            payment.initialCreditBalanceCents,
            payment.amountCents,
            false,
            [],
            [],
            false
          )
          return { ...payment, invoicesToPay: selectedInvoices }
        }
        return { ...payment }
      }),
    })
  }

  const handleAccountRowSelectionModelChange = (selection, doReset = true) => {
    const newId = get(selection, '0')
    if (!newId || newId === accountId) {
      return
    }
    const paymentInfo = requestedPayments.find(payment => payment.accountId === newId)
    if (paymentInfo) {
      handleEditClick(paymentInfo.refId, newId)
      return
    }
    if (doReset) {
      handleNewPaymentClick()
    }

    const accountInfo = existingAccounts.find(account => account.accountId === newId)
    handleSearchChange({ id: newId, unpaidInvoiceCount: get(accountInfo, 'unpaidInvoiceCount', 0) })
  }

  const increaseStep = () => setActiveStep(prevActiveStep => prevActiveStep + 1)

  const handleNextBtnClick = formData => {
    if (isPostLoading) return
    // If Fortis + new method + CC/ACH and no token yet, trigger Fortis tokenization programmatically
    if (isFortis && formData?.isNewCard && isCreditCardOrACH && !formData?.selectedPaymentTokenDetails?.accountToken) {
      setIsFortisLoading(true)
      fortisSubmitRef.current?.()
      return
    }
    if (isCreditCardOrACH && !formData?.selectedPaymentTokenDetails?.accountToken) {
      toast.error(`Please select or add a ${isCreditCard ? 'card' : 'ACH'}`)
      return
    }

    if (isAccountCredit && invoicesToPay.length === 0) {
      toast.error(T.PLEASE_SELECT_AN_INVOICE_ERROR)
      return
    }
    const accountCreditAmountCents = formatDollarsToCents(formData.accountCreditAmount || 0)

    const payload = getSinglePaymentPayload({
      ...localState,
      ...formData,
      ...getAccountFields(),
      initialCreditBalanceCents,
      appliedCreditLimitCents: accountCreditAmountCents,
    })

    if (hasInitialAccount) {
      // Single customer post call
      handlePostPayments([payload])
      return
    }

    setLocalState({
      ...cloneDeep(PAYMENT_MODEL),
      editPaymentRefId: '',
      requestedPayments: addUpdateRequestedPayments(editPaymentRefId, payload, localState.requestedPayments),
    })
    reset(cloneDeep(PAYMENT_FORM_MODEL))

    setIsFortisLoading(false)

    if (editPaymentRefId) {
      increaseStep()
      return
    }

    searchRef?.current?.focus()

    if (!hasExistingAccounts) {
      return
    }

    const lastAccountId = payload.accountId
    const lastAccountIdIndex = existingAccounts.findIndex(account => account.accountId === lastAccountId)
    const nextAccountId = get(existingAccounts, `${lastAccountIdIndex + 1}.accountId`)

    if (nextAccountId) {
      handleAccountRowSelectionModelChange([nextAccountId], false)
      return
    }
    increaseStep()
  }

  const submitFromSummaryButton = formData => {
    if (isPostLoading) return
    // If Fortis + new method + CC/ACH and no token yet, trigger Fortis tokenization programmatically
    if (isFortis && formData?.isNewCard && isCreditCardOrACH && !formData?.selectedPaymentTokenDetails?.accountToken) {
      setIsFortisLoading(true)
      fortisSubmitRef.current?.()
      return
    }
    if (isCreditCardOrACH && !formData?.selectedPaymentTokenDetails?.accountToken) {
      toast.error(`Please select or add a ${isCreditCard ? 'card' : 'ACH'}`)
      return
    }

    if (isAccountCredit && invoicesToPay.length === 0) {
      toast.error(T.PLEASE_SELECT_AN_INVOICE_ERROR)
      return
    }

    const accountCreditAmountCents = formatDollarsToCents(formData.accountCreditAmount || 0)

    const payload = getSinglePaymentPayload({
      ...localState,
      ...formData,
      ...getAccountFields(),
      initialCreditBalanceCents,
      appliedCreditLimitCents: accountCreditAmountCents,
    })

    setLocalState({
      ...cloneDeep(PAYMENT_MODEL),
      editPaymentRefId: '',
      requestedPayments: addUpdateRequestedPayments(editPaymentRefId, payload, localState.requestedPayments),
    })
    reset(cloneDeep(PAYMENT_FORM_MODEL))

    setIsFortisLoading(false)
    if (editPaymentRefId) {
      increaseStep()
      return
    }

    searchRef?.current?.focus()

    if (!hasExistingAccounts) {
      increaseStep()
      return
    }
    increaseStep()
  }

  const handleFortisTokenized = () => {
    const latest = getValues()
    if (activeStep === FIRST_STEP_INDEX) {
      handleNextBtnClick(latest)
    } else {
      submitFromSummaryButton(latest)
    }
  }

  const handleSummaryBtnClick = () => {
    const { dirtyFields, touchedFields } = formState
    const { checkNum, ...restTouchedFields } = touchedFields

    if (
      accountId &&
      watchPaymentMethod &&
      (!isEmpty(dirtyFields) ||
        !isEmpty(restTouchedFields) ||
        requestedPayments.length === 0 ||
        (watchPaymentMethod === ACCOUNT_CREDIT && watchAmount !== 0))
    ) {
      const submit = handleSubmit(submitFromSummaryButton)
      submit()
    } else if (requestedPayments.length > 0) {
      increaseStep()
    }
  }

  const onCloseEditBillingLocationDialog = useCallback(() => {
    if (beneficialOwnerContact) {
      setIsOpenEditBeneficialLocationDialog(false)
    } else {
      setIsOpenEditBillingLocationDialog(false)
    }
  }, [beneficialOwnerContact])

  const onSaveEditBillingLocationDialog = useCallback(() => {
    if (beneficialOwnerContact) {
      setIsOpenEditBeneficialLocationDialog(false)
    } else {
      setIsOpenEditBillingLocationDialog(false)
    }
    if (accountId) {
      getAccountDetails({ accountId })
        .unwrap()
        .then(response => {
          if (isCalledFromCustomerDetailsPage) {
            dispatch(putCustomerDetails(response))
          }
        })
    }
  }, [beneficialOwnerContact, accountId])

  const onOpenEditBillingLocationDialog = useCallback(() => {
    if (beneficialOwnerContact) {
      setIsOpenEditBeneficialLocationDialog(true)
    } else {
      setIsOpenEditBillingLocationDialog(true)
    }
  }, [beneficialOwnerContact])

  const handleConfirmClose = () => {
    onClose()
    setIsOpenConfirmCloseDialog(false)
  }

  const handleCancelClose = () => {
    setIsOpenConfirmCloseDialog(false)
  }

  useEffect(() => {
    getIsSurchargeApplied()
  }, [])

  useEffect(() => {
    if (hasInitialAccount && canTakePayment) {
      handleSearchChange({ ...initialAccount, unpaidInvoiceCount: 1 })
    }
  }, [hasInitialAccount, canTakePayment])

  useEffect(() => {
    if (isOpen) {
      setLocalState({ groupId: initialGroupId })
    }
  }, [isOpen])

  const billingAddressForPayment = useMemo(() => {
    const billingAddress = get(accountData, 'billingAddress')
    const beneficialOwnerAddress = get(beneficialOwnerContact, 'address')
    const isBeneficialOwnerAddressEmpty = isAddressObjectEmpty(beneficialOwnerAddress)
    const isBillingAddressEmpty = isAddressObjectEmpty(billingAddress)
    if (accountId && beneficialOwnerContact && !isBeneficialOwnerAddressEmpty) {
      return beneficialOwnerAddress
    }
    if (accountId && !isBillingAddressEmpty) {
      return billingAddress
    }
    return {}
  }, [accountData, accountId, beneficialOwnerContact])

  const billingAddressStr = useMemo(() => {
    const billingAddress = get(accountData, 'billingAddress')
    const beneficialOwnerAddress = get(beneficialOwnerContact, 'address')
    const isBeneficialOwnerAddressEmpty = isAddressObjectEmpty(beneficialOwnerAddress)
    const isBillingAddressEmpty = isAddressObjectEmpty(billingAddress)
    if (isLoadingGetAccountDetails) {
      return '-'
    }
    if (accountId && beneficialOwnerContact && !isBeneficialOwnerAddressEmpty) {
      return combineAddressInfo(beneficialOwnerAddress)
    }
    if (accountId && !isBillingAddressEmpty) {
      return combineAddressInfo(billingAddress)
    }
    if (accountId && isBillingAddressEmpty) {
      return 'No address saved'
    }
    return '-'
  }, [accountData, accountId, isLoadingGetAccountDetails, beneficialOwnerContact])

  useEffect(() => {
    if (isOpen) {
      handleAccountRowSelectionModelChange([get(existingAccounts, '0.accountId')])
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen && accountId) {
      getOverridingBeneficialOwner({
        accountId,
      })
    }
  }, [accountId, isOpen])

  const Layout = useMemo(() => (hasExistingAccounts ? ListLayout : SearchLayout), [hasExistingAccounts])

  return (
    <HHBaseDialog open={isOpen} onClose={handleClose} maxWidth="xl" fullWidth>
      <HHDialogTitle title={isFirstStep ? T.NEW_PAYMENT : T.SUMMARY} onClose={handleClose} />
      <DialogContent sx={{ px: 0 }}>
        <FormProvider {...paymentForm}>
          {isFirstStep && (
            <Layout
              isLoading={isLoading}
              accountId={accountId}
              accountData={accountData}
              savedPaymentMethods={savedPaymentMethods}
              existingAccounts={existingAccounts}
              isCustomerSearch={isCustomerSearch}
              sortedUnpaidInvoices={sortedUnpaidInvoices}
              hasInitialAccount={hasInitialAccount}
              searchRef={searchRef}
              localState={localState}
              apiRef={apiRef}
              initialCreditBalanceCents={initialCreditBalanceCents}
              updatedCreditBalanceCents={updatedCreditBalanceCents}
              selectionModel={selectionModel}
              watchAmount={watchAmount}
              hasSurcharge={hasSurcharge}
              billingAddressStr={billingAddressStr}
              billingAddress={billingAddressForPayment}
              onChange={handleChange}
              onSearchChange={handleSearchChange}
              onRowCheckboxClick={handleRowCheckboxClick}
              onSelectAllClick={handleSelectAllClick}
              onAmountChange={handleAmountChange}
              onSubmit={handleSubmit(handleNextBtnClick)}
              onClearClick={handleClearClick}
              onOpenEditBillingLocationDialog={onOpenEditBillingLocationDialog}
              onAccountRowSelectionModelChange={handleAccountRowSelectionModelChange}
              onAccountCreditAmountChange={handleAccountCreditAmountChange}
              isBeneficialOwnerAddress={isBeneficialOwnerAddress}
              fortisSubmitRef={fortisSubmitRef}
              onFortisTokenized={handleFortisTokenized}
              onFortisLoadingChange={setIsFortisLoading}
            />
          )}
        </FormProvider>

        {!isFirstStep && (
          <Summary
            requestedPayments={requestedPayments}
            batchDate={batchDate}
            groupId={groupId}
            showAddToGroupField={!initialGroupId}
            onChange={handleChange}
            onEditClick={handleEditClick}
            onRemoveClick={handleRemoveClick}
          />
        )}

        <Box id="takePaymentGroup" className="takePaymentGroup" display="none" />
      </DialogContent>
      <DialogActions
        accountId={accountId}
        isPostLoading={isPostLoading || isFortisLoading}
        isFirstStep={isFirstStep}
        isSingleStep={hasInitialAccount}
        paymentMethod={watchPaymentMethod}
        requestedPayments={requestedPayments}
        onClose={handleClose}
        onNextBtnClick={handleSubmit(handleNextBtnClick)}
        onPostBtnClick={() => handlePostPayments(requestedPayments)}
        onSummaryBtnClick={handleSummaryBtnClick}
        onNewPaymentClick={handleNewPaymentClick}
        editPaymentRefId={editPaymentRefId}
      />
      <EditBillingLocationModal
        customerDetails={accountData}
        open={isOpenEditBillingLocationDialog}
        onClose={onCloseEditBillingLocationDialog}
        onSave={onSaveEditBillingLocationDialog}
      />
      <EditBeneficialOwnerLocationDialog
        accountId={accountId}
        id={beneficialOwnerId}
        open={isOpenEditBeneficialLocationDialog}
        onClose={onCloseEditBillingLocationDialog}
        onSave={onSaveEditBillingLocationDialog}
      />
      <HHConfirmDialog
        isOpen={isOpenConfirmCloseDialog}
        DialogProps={{ maxWidth: 'xs' }}
        ConfirmButtonProps={{ size: 'small' }}
        confirmButtonTitle="Confirm"
        confirmTitle="Unposted payments"
        confirmDescription={`You currently have ${requestedPayments.length} ${
          requestedPayments.length === 1 ? 'payment' : 'payments'
        } added in this batch that has not been posted.  If you proceed, all work will be lost.  Do you want to continue?`}
        onConfirm={handleConfirmClose}
        onClose={handleCancelClose}
      />
    </HHBaseDialog>
  )
}

TakePaymentsDialog.propTypes = {
  isOpen: PropTypes.bool,
  initialGroupId: PropTypes.string,
  initialAccount: PropTypes.object,
  existingAccounts: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  callback: PropTypes.func,
}

export default TakePaymentsDialog
